/**
 * One tile of the plan's facts strip (user, 19/10/2026: "cleaner, more
 * professional"). Every tile has the same four rows: the label (icon, name,
 * ⓘ — its pencil or chevron shows on hover), one value in one typeface, one
 * detail line, and one status line with a dot (grey idle, green live or
 * done, amber when something needs a look). A tile with an action is
 * clickable all over — an invisible button stretched across it — while its
 * ⓘ stays hoverable.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { ChevronDown, Pencil, type LucideIcon } from 'lucide-react';

import { HintDot } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { FactTone } from '../facts/generation-fact';

export interface TileAction {
  /** What the click does, for screen readers and the tooltip. */
  label: string;
  onClick: () => void;
  /** A panel it opens and closes (chevron); absent = a dialog to edit (pencil). */
  expanded?: boolean;
}

export interface TileStatus {
  text: ReactNode;
  tone: FactTone;
}

interface Props {
  icon: LucideIcon;
  label: string;
  hint?: string;
  action?: TileAction;
  detail?: ReactNode;
  status: TileStatus;
  children: ReactNode;
  testId?: string;
}

const DOT: Record<FactTone, string> = {
  muted: 'bg-muted-foreground/40',
  live: 'bg-success animate-pulse motion-reduce:animate-none',
  success: 'bg-success',
  warning: 'bg-warning',
};
const STATUS_TEXT: Record<FactTone, string> = { muted: 'text-muted-foreground', live: 'text-foreground', success: 'text-foreground', warning: 'text-warning' };

const StretchedButton: FC<{ action: TileAction }> = ({ action }) => (
  <button
    type="button"
    onClick={action.onClick}
    aria-label={action.label}
    title={action.label}
    aria-expanded={action.expanded}
    className="absolute inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
  />
);

const TileLabel: FC<Pick<Props, 'icon' | 'label' | 'hint' | 'action'>> = ({ icon: Icon, label, hint, action }) => {
  const Corner = action?.expanded === undefined ? Pencil : ChevronDown;
  return (
    <span className="pointer-events-none relative flex h-4 items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      {label}
      {hint ? (
        <span className="pointer-events-auto opacity-70 transition-opacity hover:opacity-100">
          <HintDot label={hint} />
        </span>
      ) : null}
      {action ? (
        <Corner
          className={cn('ml-auto h-3 w-3 transition', action.expanded ? 'rotate-180 opacity-80' : 'opacity-0 group-hover:opacity-80 group-focus-within:opacity-80')}
          aria-hidden
        />
      ) : null}
    </span>
  );
};

const StatusLine: FC<{ status: TileStatus }> = ({ status }) => (
  <span className={cn('pointer-events-none relative flex min-w-0 items-center gap-1.5 text-[11px]', STATUS_TEXT[status.tone])}>
    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT[status.tone])} aria-hidden />
    <span className="truncate">{status.text}</span>
  </span>
);

export const FactTile: FC<Props> = ({ action, detail, status, children, testId, ...label }) => (
  <div className={cn('group relative flex min-w-0 flex-col gap-1.5 bg-card px-4 py-3', action && 'transition-colors hover:bg-muted/30')} data-testid={testId}>
    {action ? <StretchedButton action={action} /> : null}
    <TileLabel {...label} action={action} />
    <div className={cn('relative flex h-6 min-w-0 items-center gap-2 text-[15px] font-semibold tabular-nums text-foreground', action && 'pointer-events-none')}>{children}</div>
    <div className="pointer-events-none relative -mt-1 min-w-0 truncate text-xs tabular-nums text-muted-foreground">{detail ?? ' '}</div>
    <StatusLine status={status} />
  </div>
);
